/*
  home.js: behaviour for home-* sections.

  - Add to cart goes through Impulse, exactly as on the product page
    (assets/pdp.js): each card or bundle form is handed to theme.AjaxProduct,
    which posts to /cart/add.js and fires ajaxProduct:added. Impulse's
    CartDrawer rebuilds and opens on that event, and Shopify's own analytics
    see the /cart/add.js request, so tracking fires as on any product page.
  - Hero slider: plain CSS scroll-snap; this file drives the dots, arrows,
    slide-link tap guard and autoplay. Autoplay pauses while hovered,
    touched, keyboard-focused (slide links, arrows, dots), off screen or in
    a hidden tab; stops for the visit after a swipe, drag, horizontal wheel,
    arrow or dot; never runs under prefers-reduced-motion. The active dot
    shows the slide's progress; a pause button sits beside the dots.
  - Product tabs: every panel is rendered by Liquid; this file switches them.
  - Rails (rows of cards): prev/next arrows on desktop when a row holds more
    cards than fit.
  - Sign-up: posts to Klaviyo's client subscriptions endpoint with the list
    ID from the section and the public key from the Klaviyo script on the
    page; otherwise the Shopify customer form submits as normal.
  Loaded with defer after theme.js, so theme.AjaxProduct exists by now.
*/
(function () {
  'use strict';

  // Every home-* section loads this file; run it once.
  if (window.homeScriptLoaded) return;
  window.homeScriptLoaded = true;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Add to cart ---------- */

  function bindCartForm(form) {
    if (form.dataset.homeCartBound) return;
    form.dataset.homeCartBound = 'true';
    var button = form.querySelector('[data-home-add]');
    if (!button) return;

    if (window.theme && typeof theme.AjaxProduct === 'function') {
      new theme.AjaxProduct(form, '[data-home-add]');
      return;
    }

    // Fallback if a theme update ever removes theme.AjaxProduct: same request,
    // same event, so the drawer still opens.
    form.addEventListener('submit', function (evt) {
      evt.preventDefault();
      button.classList.add('btn--loading');
      var cartAdd = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js';
      fetch(cartAdd, {
        method: 'POST',
        body: new FormData(form),
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' }
      })
        .then(function (response) { return response.json(); })
        .then(function (data) {
          button.classList.remove('btn--loading');
          if (data.status && data.status >= 400) {
            showFormError(form, data.description || data.message);
            return;
          }
          if (!document.getElementById('CartDrawer')) {
            window.location.href = (window.theme && theme.routes && theme.routes.cartPage) || '/cart';
            return;
          }
          form.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: { product: data, addToCartBtn: button },
            bubbles: true
          }));
        })
        .catch(function () { button.classList.remove('btn--loading'); });
    });
  }

  function showFormError(form, message) {
    var old = form.querySelector('.errors');
    if (old) old.remove();
    if (!message) return;
    var div = document.createElement('div');
    div.className = 'errors';
    div.textContent = typeof message === 'string' ? message : String(message);
    form.append(div);
  }

  /* ---------- Hero slider ---------- */

  function Slider(root) {
    this.root = root;
    this.track = root.querySelector('[data-vg-slides]');
    this.slides = Array.prototype.slice.call(this.track.children);
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-vg-dot]'));
    this.interval = Number(root.dataset.autoplay || 0) * 1000;
    this.paused = false;
    this.timer = null;

    var self = this;
    this.dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { self.go(i); });
    });
    var prev = root.querySelector('[data-vg-prev]');
    var next = root.querySelector('[data-vg-next]');
    this.dots.forEach(function (dot) {
      dot.addEventListener('click', function () { self.halt(); });
    });
    if (prev) prev.addEventListener('click', function () { self.halt(); self.go(self.current() - 1); });
    if (next) next.addEventListener('click', function () { self.halt(); self.go(self.current() + 1); });

    var ticking = false;
    this.track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; self.mark(); });
    }, { passive: true });

    // Slide links: a tap follows the link; a swipe or drag that moved the
    // pointer, or scrolled the track, does not.
    var start = null;
    this.track.addEventListener('pointerdown', function (evt) {
      start = { x: evt.clientX, y: evt.clientY, scroll: self.track.scrollLeft };
    }, { passive: true });
    this.track.addEventListener('click', function (evt) {
      var link = evt.target.closest && evt.target.closest('[data-vg-slide-link]');
      if (!link || !start || evt.detail === 0) return; // detail 0: keyboard
      var moved = Math.abs(evt.clientX - start.x) > 10 || Math.abs(evt.clientY - start.y) > 10;
      var scrolled = Math.abs(self.track.scrollLeft - start.scroll) > 2;
      if (moved || scrolled) evt.preventDefault();
      start = null;
    });

    // Always start on slide 1 (browsers may restore the old position).
    this.track.scrollLeft = 0;
    window.addEventListener('pageshow', function (evt) {
      if (!evt.persisted) return;
      self.track.scrollLeft = 0;
      self.elapsed = 0;
      self.mark();
    });

    this.mark();
    this.initAutoplay();
  }

  Slider.prototype = {
    current: function () {
      var width = this.track.clientWidth || 1;
      return Math.round(this.track.scrollLeft / width);
    },

    go: function (index) {
      var n = this.slides.length;
      var i = ((index % n) + n) % n;
      this.track.scrollTo({
        left: i * this.track.clientWidth,
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    },

    mark: function () {
      var cur = this.current();
      if (cur !== this.marked) { this.marked = cur; this.elapsed = 0; }
      this.dots.forEach(function (dot, i) { dot.setAttribute('aria-current', String(i === cur)); });
      this.slides.forEach(function (slide, i) {
        if (i === cur) slide.removeAttribute('inert');
        else slide.setAttribute('inert', '');
      });
    },

    /* Autoplay. One clock (requestAnimationFrame) counts only while nothing
       pauses it, and drives both the slide change and the dot's progress
       fill, so the two can never drift apart. */
    initAutoplay: function () {
      var self = this;
      this.pauseButton = this.root.querySelector('[data-vg-pause]');
      this.halted = false;   // stopped by the shopper (gesture, arrow, dot, pause button)
      this.reasons = {};     // temporary pauses: hover, touch, focus, view, hidden
      this.elapsed = 0;
      if (!this.interval || this.slides.length < 2 || (window.Shopify && Shopify.designMode)) return;
      this.enabled = true;

      var hold = function (reason, on) {
        if (on) self.reasons[reason] = true; else delete self.reasons[reason];
        self.render();
      };

      // Hover (mouse only) and touch.
      this.root.addEventListener('pointerenter', function (evt) { if (evt.pointerType === 'mouse') hold('hover', true); });
      this.root.addEventListener('pointerleave', function (evt) { if (evt.pointerType === 'mouse') hold('hover', false); });
      // Safety net: any mouse movement outside the slider ends the hover
      // (a pointerleave can be missed when the pointer jumps).
      document.addEventListener('pointermove', function (evt) {
        if (self.reasons.hover && evt.pointerType === 'mouse' && !self.root.contains(evt.target)) hold('hover', false);
      }, { passive: true });
      this.root.addEventListener('touchstart', function () { hold('touch', true); }, { passive: true });
      var release = function () { hold('touch', false); };
      this.root.addEventListener('touchend', release, { passive: true });
      this.root.addEventListener('touchcancel', release, { passive: true });

      // Keyboard focus on a slide link, arrow or dot (not the pause button).
      var focusable = '[data-vg-slide-link], [data-vg-prev], [data-vg-next], [data-vg-dot]';
      this.root.addEventListener('focusin', function (evt) {
        var el = evt.target;
        if (el.matches && el.matches(focusable) && el.matches(':focus-visible')) hold('focus', true);
      });
      this.root.addEventListener('focusout', function () { hold('focus', false); });

      // Off screen, and hidden tab.
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          hold('view', !entries[0].isIntersecting);
        }).observe(this.root);
      }
      document.addEventListener('visibilitychange', function () { hold('hidden', document.hidden); });
      hold('hidden', document.hidden);

      // A sideways swipe or drag (more than 10px, more across than down) or
      // a horizontal wheel stops autoplay for the visit. A tap, or scrolling
      // the page up and down over the slider, does not.
      var press = null;
      this.track.addEventListener('pointerdown', function (evt) { press = { x: evt.clientX, y: evt.clientY }; }, { passive: true });
      var moved = function (x, y) {
        if (!press) return;
        var dx = Math.abs(x - press.x), dy = Math.abs(y - press.y);
        if (dx > 10 && dx > dy) self.halt();
      };
      this.track.addEventListener('pointermove', function (evt) { if (evt.buttons || evt.pointerType !== 'mouse') moved(evt.clientX, evt.clientY); }, { passive: true });
      this.track.addEventListener('touchstart', function (evt) { var p = evt.touches[0]; if (p) press = { x: p.clientX, y: p.clientY }; }, { passive: true });
      this.track.addEventListener('touchmove', function (evt) { var p = evt.touches[0]; if (p) moved(p.clientX, p.clientY); }, { passive: true });
      var clear = function () { press = null; };
      this.track.addEventListener('pointerup', clear, { passive: true });
      this.track.addEventListener('pointercancel', clear, { passive: true });
      this.track.addEventListener('wheel', function (evt) {
        if (Math.abs(evt.deltaX) > Math.abs(evt.deltaY) && Math.abs(evt.deltaX) > 2) self.halt();
      }, { passive: true });

      // Pause / play button.
      if (this.pauseButton) {
        this.pauseButton.addEventListener('click', function () {
          if (self.halted) { self.halted = false; self.elapsed = 0; } else { self.halted = true; }
          self.render();
        });
      }

      // Reduced motion: no autoplay, no progress, no button.
      var motion = function () { self.render(); };
      if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', motion);

      this.render();
      var last = null;
      var tick = function (now) {
        var dt = last === null ? 0 : Math.min(now - last, 100);
        last = now;
        if (self.running()) {
          self.elapsed += dt;
          if (self.elapsed >= self.interval) {
            self.elapsed = 0;
            self.go(self.current() + 1); // loops back to slide 1 after the last
          }
        }
        self.progress();
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    },

    running: function () {
      return this.enabled && !this.halted && !reducedMotion.matches && Object.keys(this.reasons).length === 0;
    },

    halt: function () {
      if (!this.enabled || this.halted) return;
      this.halted = true;
      this.elapsed = 0;
      this.render();
    },

    render: function () {
      if (!this.enabled) return;
      var on = !this.halted && !reducedMotion.matches;
      this.root.classList.toggle('vg-autoplay-on', on);
      this.root.classList.toggle('vg-autoplay-paused', on && !this.running());
      if (this.pauseButton) {
        this.pauseButton.hidden = reducedMotion.matches;
        this.pauseButton.setAttribute('aria-label', this.halted ? 'Play slideshow' : 'Pause slideshow');
        this.pauseButton.dataset.state = this.halted ? 'play' : 'pause';
      }
      this.progress();
    },

    progress: function () {
      var cur = this.current();
      var pct = this.interval ? Math.min(100, (this.elapsed / this.interval) * 100) : 0;
      this.dots.forEach(function (dot, i) {
        dot.style.setProperty('--vg-progress', (i === cur ? pct : 0).toFixed(1) + '%');
      });
    },

    // Theme editor: show the slide being edited.
    select: function (slide) {
      var i = this.slides.indexOf(slide);
      if (i > -1) this.go(i);
    }
  };

  /* ---------- Tabs ---------- */

  function Tabs(root) {
    this.tabs = Array.prototype.slice.call(root.querySelectorAll('[role="tab"]'));
    var self = this;
    this.tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { self.select(i, false); });
      tab.addEventListener('keydown', function (evt) {
        var n = self.tabs.length;
        var to = null;
        if (evt.key === 'ArrowRight') to = (i + 1) % n;
        else if (evt.key === 'ArrowLeft') to = (i - 1 + n) % n;
        else if (evt.key === 'Home') to = 0;
        else if (evt.key === 'End') to = n - 1;
        if (to === null) return;
        evt.preventDefault();
        self.select(to, true);
      });
    });
  }

  Tabs.prototype.select = function (index, focus) {
    this.tabs.forEach(function (tab, i) {
      var on = i === index;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
      var panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (!panel) return;
      panel.hidden = !on;
      if (on) {
        var rail = panel.querySelector('[data-vg-rail]');
        if (rail) { rail.scrollLeft = 0; updateRail(rail); }
      }
    });
    var current = this.tabs[index];
    if (focus) current.focus();
    current.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  };

  /* ---------- Rails ---------- */

  function updateRail(rail) {
    var wrap = rail.closest('[data-vg-rail-wrap]');
    if (!wrap) return;
    var max = rail.scrollWidth - rail.clientWidth;
    var overflow = max > 2;
    wrap.classList.toggle('is-overflowing', overflow);
    var prev = wrap.querySelector('[data-vg-rail-prev]');
    var next = wrap.querySelector('[data-vg-rail-next]');
    if (prev) prev.disabled = !overflow || rail.scrollLeft <= 2;
    if (next) next.disabled = !overflow || rail.scrollLeft >= max - 2;
  }

  function bindRail(rail) {
    if (rail.dataset.vgRailBound) return;
    rail.dataset.vgRailBound = 'true';
    var wrap = rail.closest('[data-vg-rail-wrap]');
    if (!wrap) return;
    var step = function (dir) {
      var card = rail.firstElementChild;
      var gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      var by = card ? (card.getBoundingClientRect().width + gap) * Math.max(1, Math.floor(rail.clientWidth / (card.getBoundingClientRect().width + gap))) : rail.clientWidth;
      rail.scrollBy({ left: dir * by, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };
    var prev = wrap.querySelector('[data-vg-rail-prev]');
    var next = wrap.querySelector('[data-vg-rail-next]');
    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });
    rail.addEventListener('scroll', function () { updateRail(rail); }, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(function () { updateRail(rail); }).observe(rail);
    updateRail(rail);
  }

  /* ---------- Sign-up (Klaviyo) ---------- */

  var KLAVIYO_REVISION = '2024-10-15';

  // The Klaviyo app embed loads klaviyo.js?company_id=<public key>.
  function klaviyoPublicKey() {
    var scripts = document.querySelectorAll('script[src*="klaviyo.js"]');
    for (var i = 0; i < scripts.length; i++) {
      try {
        var key = new URL(scripts[i].src, location.href).searchParams.get('company_id');
        if (key) return key;
      } catch (e) { /* ignore malformed src */ }
    }
    return null;
  }

  function bindSignup(root) {
    if (root.dataset.vgSignupBound) return;
    root.dataset.vgSignupBound = 'true';
    var list = root.dataset.list;
    var form = root.querySelector('form');
    if (!list || !form) return; // No list ID: the Shopify customer form submits as normal.

    var input = form.querySelector('input[type="email"]');
    var button = form.querySelector('button[type="submit"]');
    var status = form.querySelector('[data-vg-signup-status]');

    function say(message, kind) {
      if (!status) return;
      status.textContent = message;
      status.classList.toggle('is-error', kind === 'error');
      status.classList.toggle('is-success', kind === 'success');
    }

    form.addEventListener('submit', function (evt) {
      var key = klaviyoPublicKey();
      if (!key || !input) return; // Fall back to the Shopify form.
      evt.preventDefault();

      var email = input.value.trim();
      if (!email || !input.checkValidity()) {
        input.setAttribute('aria-invalid', 'true');
        say(root.dataset.error, 'error');
        input.focus();
        return;
      }
      input.removeAttribute('aria-invalid');
      button.classList.add('btn--loading');
      button.disabled = true;

      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(key), {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', 'revision': KLAVIYO_REVISION },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: root.dataset.source || 'Homepage sign-up',
              profile: { data: { type: 'profile', attributes: { email: email } } }
            },
            relationships: { list: { data: { type: 'list', id: list } } }
          }
        })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Klaviyo ' + response.status);
          button.classList.remove('btn--loading');
          button.disabled = false;
          form.reset();
          say(root.dataset.success, 'success');
        })
        .catch(function () {
          // Klaviyo unreachable or refused: send it through Shopify instead.
          form.submit();
        });
    });
  }

  /* ---------- Init ---------- */

  var sliders = new WeakMap();

  function init(scope) {
    scope = scope || document;
    scope.querySelectorAll('.vg-home form.vg-cart-form').forEach(bindCartForm);
    scope.querySelectorAll('[data-vg-slider]').forEach(function (root) {
      if (sliders.has(root)) return;
      sliders.set(root, new Slider(root));
    });
    scope.querySelectorAll('[data-vg-tabs]').forEach(function (root) {
      if (root.dataset.vgTabsBound) return;
      root.dataset.vgTabsBound = 'true';
      new Tabs(root);
    });
    scope.querySelectorAll('[data-vg-rail]').forEach(bindRail);
    scope.querySelectorAll('[data-vg-signup]').forEach(bindSignup);
  }

  init(document);
  document.addEventListener('shopify:section:load', function (evt) { init(evt.target); });
  document.addEventListener('shopify:block:select', function (evt) {
    var slider = evt.target.closest && evt.target.closest('[data-vg-slider]');
    if (slider && sliders.has(slider)) sliders.get(slider).select(evt.target);
    var panel = evt.target.closest && evt.target.closest('[role="tabpanel"]');
    var tab = evt.target.matches && evt.target.matches('[role="tab"]') ? evt.target : null;
    if (tab) tab.click();
    else if (panel) {
      var owner = document.querySelector('[aria-controls="' + panel.id + '"]');
      if (owner) owner.click();
    }
  });
})();
